// Empresa de un trabajador — helpers del filtro de la pantalla de Equipo.
//
// Solo el superadmin (type 5) ve trabajadores de VARIAS empresas a la vez
// (searchWorkers('') devuelve todas las personas del sistema y cada fila trae
// companyId). Como los nombres se repiten entre empresas, necesita ver y
// filtrar por empresa.
//
// Reglas:
//   · El catálogo de empresas (searchCompanies) es solo para poner NOMBRES.
//     Si esa petición falla, las opciones se derivan igualmente de las filas
//     y se etiquetan con el id («Empresa #7»): el filtro sigue funcionando.
//   · Una fila sin companyId NO se esconde: cae en la opción «Sin empresa».
//   · Solo se ofrecen empresas PRESENTES en las filas cargadas; ofrecer
//     empresas sin plantilla visible solo genera resultados vacíos.

export const ALL_COMPANIES_VALUE = 'todos';
export const NO_COMPANY_VALUE = 'sin-empresa';
export const NO_COMPANY_LABEL = 'Sin empresa';

// Id de empresa normalizado a string (o null si la fila no lo trae). Los ids
// llegan unas veces como número y otras como texto según el endpoint.
export const workerCompanyId = (worker) => {
  const raw = worker?.companyId;
  if (raw == null || raw === '') return null;
  return String(raw);
};

// Map id → nombre a partir del catálogo de empresas. Tolera lista vacía,
// nula o con entradas rotas (la petición puede haber fallado).
export const companyNameMap = (companies) => {
  const map = new Map();
  for (const c of Array.isArray(companies) ? companies : []) {
    if (!c || c.id == null) continue;
    const name = String(c.name || '').trim();
    map.set(String(c.id), name || `Empresa #${c.id}`);
  }
  return map;
};

// Etiqueta visible de la empresa de un trabajador. Sin nombre conocido
// caemos al id; sin id, a «Sin empresa».
export const companyLabel = (worker, names) => {
  const id = workerCompanyId(worker);
  if (id == null) return NO_COMPANY_LABEL;
  const name = names instanceof Map ? names.get(id) : names?.[id];
  return name || `Empresa #${id}`;
};

// Opciones del selector, ordenadas alfabéticamente (locale español). «Sin
// empresa» va SIEMPRE al final y solo si hay alguna fila huérfana.
export const buildCompanyOptions = (workers, companies) => {
  const names = companyNameMap(companies);
  const seen = new Map();
  let hasOrphans = false;
  for (const w of Array.isArray(workers) ? workers : []) {
    const id = workerCompanyId(w);
    if (id == null) { hasOrphans = true; continue; }
    if (!seen.has(id)) seen.set(id, companyLabel(w, names));
  }
  const options = Array.from(seen, ([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  if (hasOrphans) options.push({ value: NO_COMPANY_VALUE, label: NO_COMPANY_LABEL });
  return options;
};

// ¿La fila pasa el filtro de empresa? 'todos' (o valor vacío) no filtra.
export const matchesCompanyFilter = (worker, value) => {
  if (value == null || value === '' || value === ALL_COMPANIES_VALUE) return true;
  const id = workerCompanyId(worker);
  if (value === NO_COMPANY_VALUE) return id == null;
  return id != null && id === String(value);
};
